import React, { useState, useMemo, useRef, useEffect } from 'react';
import * as LucideIcons from 'lucide-react';
import { Search, X } from 'lucide-react';

interface IconPickerProps {
  selectedIcon?: string;
  onIconSelect: (iconName: string) => void;
  label?: string;
  className?: string;
}

// Get all available Lucide icon names
const getAllIconNames = (): string[] => {
  // Filter out non-icon exports and get icon names
  const iconNames: string[] = [];
  const excludedKeys = new Set([
    'default',
    'createLucideIcon',
    'Icon',
    'IconNode',
    'IconProps',
    'IconContext',
    'DefaultProps',
  ]);
  
  for (const key in LucideIcons) {
    // Skip excluded keys, keys ending with "Icon", and non-uppercase keys
    if (
      excludedKeys.has(key) ||
      key.endsWith('Icon') ||
      key[0] !== key[0].toUpperCase() ||
      key[0] === '_'
    ) {
      continue;
    }
    
    const value = (LucideIcons as any)[key];
    
    // Check if it's a valid React component (object with $$typeof or render)
    const isValidIcon = 
      value &&
      typeof value === 'object' &&
      (value.$$typeof || typeof value.render === 'function');
    
    if (isValidIcon) {
      iconNames.push(key);
    }
  }
  return iconNames.sort();
};

export const IconPicker: React.FC<IconPickerProps> = ({
  selectedIcon,
  onIconSelect,
  label,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIconName, setSelectedIconName] = useState<string>(selectedIcon || '');
  const popoverRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Get all available icons
  const allIcons = useMemo(() => getAllIconNames(), []);

  // Filter icons based on search term
  const filteredIcons = useMemo(() => {
    if (!searchTerm) return allIcons;
    return allIcons.filter(iconName =>
      iconName.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [allIcons, searchTerm]);

  // Update selected icon when prop changes
  useEffect(() => {
    if (selectedIcon) {
      setSelectedIconName(selectedIcon);
    }
  }, [selectedIcon]);

  // Handle icon selection
  const handleIconSelect = (iconName: string) => {
    setSelectedIconName(iconName);
    onIconSelect(iconName);
    setIsOpen(false);
    setSearchTerm('');
  };

  // Close popover when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen]);

  // Render icon component dynamically
  const renderIcon = (iconName: string, size: number = 20) => {
    try {
      const IconComponent = (LucideIcons as any)[iconName];
      // Icons are React forward ref components (objects), can be used directly
      if (IconComponent && typeof IconComponent === 'object' && (IconComponent.$$typeof || IconComponent.render)) {
        return <IconComponent size={size} />;
      }
      return null;
    } catch (error) {
      console.warn(`Icon ${iconName} not found in Lucide icons:`, error);
      return null;
    }
  };

  const SelectedIconComponent = selectedIconName
    ? renderIcon(selectedIconName, 20)
    : null;

  return (
    <div className={`relative ${className}`}>
      {label && (
        <label className="block text-sm font-medium text-foreground mb-2">
          {label}
        </label>
      )}
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-3 py-2 border border-input bg-background rounded-md hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
      >
        <div className="flex items-center space-x-2">
          {SelectedIconComponent ? (
            <>
              {SelectedIconComponent}
              <span className="text-sm">{selectedIconName}</span>
            </>
          ) : (
            <span className="text-sm text-muted-foreground">Select icon...</span>
          )}
        </div>
        <div className="flex items-center space-x-1">
          {selectedIconName && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setSelectedIconName('');
                onIconSelect('');
              }}
              className="p-1 hover:bg-muted rounded"
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </div>
      </button>

      {isOpen && (
        <div
          ref={popoverRef}
          className="absolute z-50 mt-1 w-[500px] bg-card border border-border rounded-lg shadow-lg"
        >
          {/* Search Input */}
          <div className="p-3 border-b border-border">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search icons..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-input bg-background rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-foreground text-sm"
                autoFocus
              />
            </div>
          </div>

          {/* Selected Icon Preview */}
          {selectedIconName && (
            <div className="px-3 py-2 border-b border-border bg-muted/30">
              <div className="flex items-center space-x-2 text-sm">
                <span className="font-medium text-foreground">Selected:</span>
                {renderIcon(selectedIconName, 16)}
                <span className="text-muted-foreground">{selectedIconName}</span>
              </div>
            </div>
          )}

          {/* Icon Grid */}
          <div className="p-3 max-h-[400px] overflow-y-auto">
            {filteredIcons.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground text-sm">
                No icons found matching "{searchTerm}"
              </div>
            ) : (
              <div className="grid grid-cols-8 gap-2">
                {filteredIcons.map((iconName) => {
                  const IconComponent = renderIcon(iconName, 20);
                  const isSelected = selectedIconName === iconName;
                  
                  return (
                    <button
                      key={iconName}
                      type="button"
                      onClick={() => handleIconSelect(iconName)}
                      className={`flex flex-col items-center justify-center p-2 rounded-md border transition-all ${
                        isSelected
                          ? 'border-primary bg-primary/10'
                          : 'border-border hover:border-primary/50 hover:bg-muted/50'
                      }`}
                      title={iconName}
                    >
                      {IconComponent}
                      <span className="text-[10px] text-muted-foreground mt-1 truncate w-full text-center">
                        {iconName.replace(/([A-Z])/g, ' $1').trim()}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-3 py-2 border-t border-border bg-muted/30">
            <div className="text-xs text-muted-foreground text-center">
              Showing {filteredIcons.length} of {allIcons.length} icons
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

